import * as THREE from 'three';

export type CameraMode = 'walking' | 'driving' | 'combat';

export interface CameraContextInput {
  readonly isDriving: boolean;
  readonly isAiming: boolean;
  readonly velocity: THREE.Vector2;
}

export interface CameraContext {
  readonly mode: CameraMode;
  readonly viewHeight: number;
  readonly followResponse: number;
  readonly deadZone: number;
  readonly lookAhead: THREE.Vector2;
}

export const CAMERA_CONTEXT = {
  walkingHeight: 34,
  drivingHeight: 48,
  combatHeight: 31,
  walkingResponse: 0.999,
  drivingResponse: 0.995,
  combatResponse: 0.9998,
  walkingDeadZone: 0.35,
  drivingDeadZone: 0.8,
  combatDeadZone: 0.12,
  maxDrivingLookAhead: 7,
  maxWalkingLookAhead: 1.2,
} as const;

export function cameraContext(input: CameraContextInput): CameraContext {
  const speed = input.velocity.length();
  if (input.isDriving) {
    return {
      mode: 'driving',
      viewHeight: CAMERA_CONTEXT.drivingHeight,
      followResponse: CAMERA_CONTEXT.drivingResponse,
      deadZone: CAMERA_CONTEXT.drivingDeadZone,
      lookAhead: lookAhead(input.velocity, Math.min(CAMERA_CONTEXT.maxDrivingLookAhead, speed * 0.85)),
    };
  }

  if (input.isAiming) {
    return {
      mode: 'combat',
      viewHeight: CAMERA_CONTEXT.combatHeight,
      followResponse: CAMERA_CONTEXT.combatResponse,
      deadZone: CAMERA_CONTEXT.combatDeadZone,
      lookAhead: new THREE.Vector2(),
    };
  }

  return {
    mode: 'walking',
    viewHeight: CAMERA_CONTEXT.walkingHeight,
    followResponse: CAMERA_CONTEXT.walkingResponse,
    deadZone: CAMERA_CONTEXT.walkingDeadZone,
    lookAhead: lookAhead(input.velocity, Math.min(CAMERA_CONTEXT.maxWalkingLookAhead, speed * 0.25)),
  };
}

function lookAhead(velocity: THREE.Vector2, distance: number): THREE.Vector2 {
  if (velocity.lengthSq() < 0.0001 || distance <= 0) return new THREE.Vector2();
  return velocity.clone().normalize().multiplyScalar(distance);
}
